// Hard-paywall modal triggered when a user hits 100% of their monthly credit
// budget. Shows the next tier they should upgrade to (with price + delta in
// credits) and routes them straight to Stripe Checkout.
//
// Used by the Hub panels (chat/image/research) and by /play after the first
// quest is completed on the free tier.
import { Loader2, Zap, X } from "lucide-react";
import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toastError } from "@/lib/errorToast";
import { PLANS, type PlanId } from "@/lib/plans";
import { trackFunnel } from "@/lib/funnelTracker";
import { useSampleWindow } from "@/hooks/useSampleWindow";

type Props = {
  open: boolean;
  onClose: () => void;
  /** Tier to upsell. Defaults to "operator" (the entry paid tier). */
  suggestedTier?: PlanId | null;
  /** Optional context line shown above the offer (e.g. "You're out of credits"). */
  reason?: string;
};

const UpgradeWallModal = ({
  open,
  onClose,
  suggestedTier = "operator",
  reason,
}: Props) => {
  const [loading, setLoading] = useState(false);
  // While the 30-min sample is active, hard-suppress every upsell modal.
  // The countdown chip + post-expiry modal already communicate the model.
  const { suppressUpsells } = useSampleWindow();
  if (suppressUpsells && open) {
    // Render nothing — parent doesn't need to know we suppressed.
    return null;
  }
  const targetId = suggestedTier && suggestedTier !== "script_kiddie" ? suggestedTier : "operator";
  const target = PLANS[targetId];

  const handleUpgrade = async () => {
    setLoading(true);
    // Conversion analytics: every tap of an upgrade CTA. We dedupe per click
    // (Date.now()) so repeated taps from the same modal still all count —
    // useful for spotting Stripe-redirect failures (many clicks, no checkout).
    void trackFunnel("upgrade_click", {
      plan: targetId,
      source: "upgrade_wall",
      reason: reason ?? null,
      dedupeKey: `upgrade_wall:${targetId}:${Date.now()}`,
    });
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { plan: targetId },
      });
      if (error) throw new Error(error.message ?? "Checkout failed");
      if (data?.error === "stripe_misconfigured") {
        toastError(
          "Billing is temporarily unavailable. Please try again in a few minutes.",
          { title: "Billing temporarily unavailable" },
        );
        return;
      }
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error(data?.message ?? "Checkout did not return a redirect URL");
    } catch (e) {
      toastError(e instanceof Error ? e.message : "Could not start checkout", {
        title: "Upgrade failed",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-lg p-0 overflow-hidden border-primary/40">
        {/* Header */}
        <div className="bg-gradient-to-br from-primary/20 via-primary/5 to-transparent p-6 pb-4 relative">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-wider text-primary mb-3">
            <Zap className="w-3 h-3" /> Tier up
          </div>
          <h2 className="font-mono text-xl font-bold text-foreground mb-1">
            {reason ?? "You've outgrown your tier."}
          </h2>
          <p className="text-sm text-muted-foreground font-mono">
            Level up to{" "}
            <span className="text-primary font-semibold">{target.name}</span> to keep
            running AI without limits this month.
          </p>
        </div>

        {/* Compact tier card — one price, two bullets, one CTA. The
            full feature list lives on /pricing; this modal exists to
            convert in-the-moment, not to comparison-shop. */}
        <div className="p-6 pt-2 space-y-4">
          <div className="rounded-lg border border-border bg-card/60 p-4 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="font-mono font-bold text-foreground text-base">
                {target.name}
              </p>
              <p className="text-[11px] text-muted-foreground font-mono mt-0.5 truncate">
                {target.tagline}
              </p>
            </div>
            <p className="font-mono shrink-0">
              <span className="text-2xl font-bold text-foreground">
                {target.priceLabel}
              </span>
              <span className="text-xs text-muted-foreground">/mo</span>
            </p>
          </div>

          <ul className="space-y-1.5">
            {target.features.slice(0, 2).map((f) => (
              <li
                key={f}
                className="text-xs text-foreground/90 flex items-start gap-2 font-mono"
              >
                <span className="text-primary mt-0.5">▸</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <button
            onClick={handleUpgrade}
            disabled={loading}
            className="w-full rounded-md bg-primary text-primary-foreground py-2.5 text-sm font-mono font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Redirecting…
              </>
            ) : (
              `Upgrade to ${target.name} · ${target.priceLabel}/mo`
            )}
          </button>

          <p className="text-[10px] font-mono text-muted-foreground text-center">
            Cancel anytime · See full features on{" "}
            <a href="/pricing" className="underline hover:text-foreground">
              pricing
            </a>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeWallModal;
